import { useState } from "react";
import { Sparkles, Loader2, Copy, ClipboardCheck, Wand2, ShieldCheck, Layers, Gauge } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";

const ENGINES = ["Unity", "Unreal Engine 5", "Godot 4", "Web (JavaScript/TypeScript)", "Python", "Other"];
const LANGS = { Unity: "C#", "Unreal Engine 5": "C++ / Blueprint", "Godot 4": "GDScript", "Web (JavaScript/TypeScript)": "TypeScript", Python: "Python", Other: "" };
const LEVELS = ["beginner", "intermediate", "advanced"];
const MIDDLEWARE = ["none", "FMOD", "Wwise", "Engine audio only"];

const CHECKLIST = [
  { icon: ClipboardCheck, title: "A clear brief", text: "What must the music do? Sync with narrative events, react to intensity, give each character a leitmotif, loop seamlessly in menus…" },
  { icon: Layers, title: "Your engine & level", text: "Unity, Unreal, Godot, web or Python — and whether you use FMOD / Wwise. Tell us if you are a beginner: the assistant adapts the depth of the code." },
  { icon: Gauge, title: "Output & constraints", text: "Stems for dynamic layering? Loop-ready WAV? Max latency, mobile build, offline fallback, budget of credits per month (API key caps)." },
  { icon: ShieldCheck, title: "An API key", text: "Create a game API key in Dashboard › API Keys (monthly cap + alerts). Keep it server-side or in a config file, never in a public repo." },
];

const PROMPT_TEMPLATE = `I am building a [game / app] with [Unity / Unreal / Godot / web / Python].
Goal: integrate MusicFlow to [generate leitmotifs per character / react to intensity / sync music with story events].

Technical details:
- Language: [C# / C++ / GDScript / TypeScript / Python]
- Audio middleware: [none / FMOD / Wwise]
- Expected output: [streamed MP3 / WAV stems / seamless loop]
- Constraints: [latency < 200 ms on scene load, mobile build, offline fallback]

Concrete moment: [trigger the boss theme at 2:15 of the cutscene, with a "tension" variation if the player made choice X].`;

const PRACTICES = [
  ["Modularity", "Generate with stems: true and mute/unmute drums, bass and melody to create calm / tense / victory variations of the same theme without new API calls."],
  ["State management", "Map your gameplay states (explore, combat, boss) to adaptive/transition and drive intensity (0 → 1) from health, enemy count or timer — the SDKs cross-fade for you."],
  ["Latency", "Pre-generate the tracks you need at build time or on level load (they take ~60 s); only generate at runtime for non-blocking moments, and cache downloads locally."],
  ["Fallback", "Ship a default track per state inside the build. If the API is unreachable or the monthly cap is hit, the SDK falls back to it — the player never hears silence."],
  ["Alignment", "Use metadata.bpm and metadata.key to schedule stingers on the beat and keep SFX / voice layers in tune."],
];

function Markdown({ text }) {
  const parts = text.split(/```([\w+#-]*)\n([\s\S]*?)```/g);
  const out = [];
  for (let i = 0; i < parts.length; i += 3) {
    if (parts[i]) out.push(<MdText key={`t${i}`} text={parts[i]} />);
    if (parts[i + 2] !== undefined) {
      out.push(
        <div key={`c${i}`} className="relative group my-3 rounded-xl border border-border bg-background/70 overflow-hidden">
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-border text-[11px] font-mono text-muted-foreground">
            <span>{parts[i + 1] || "code"}</span>
            <button type="button" onClick={() => { navigator.clipboard.writeText(parts[i + 2]); toast.success("Code copied."); }} className="hover:text-primary flex items-center gap-1" data-testid="assistant-copy-code"><Copy size={12} /> Copy</button>
          </div>
          <pre className="p-4 overflow-x-auto text-xs font-mono leading-relaxed text-foreground/90">{parts[i + 2]}</pre>
        </div>
      );
    }
  }
  return <div className="text-sm leading-relaxed">{out}</div>;
}

function MdText({ text }) {
  return text.split("\n").filter((l) => l.trim() !== "").map((line, i) => {
    const h = line.match(/^(#{1,4})\s+(.*)/);
    if (h) return <p key={i} className={`font-semibold mt-4 mb-1 ${h[1].length <= 2 ? "text-base text-primary" : "text-sm"}`}>{h[2].replace(/\*\*/g, "")}</p>;
    const li = line.match(/^\s*(?:[-*]|\d+\.)\s+(.*)/);
    const body = (li ? li[1] : line).split(/(`[^`]+`|\*\*[^*]+\*\*)/g).map((seg, j) => {
      if (seg.startsWith("`")) return <code key={j} className="font-mono text-[12px] bg-muted px-1 py-0.5 rounded">{seg.slice(1, -1)}</code>;
      if (seg.startsWith("**")) return <strong key={j}>{seg.slice(2, -2)}</strong>;
      return seg;
    });
    return li ? <p key={i} className="pl-4 my-0.5">• {body}</p> : <p key={i} className="my-1.5 text-foreground/90">{body}</p>;
  });
}

export default function IntegrationGuide() {
  const [form, setForm] = useState({ app_type: "video game", engine: "Unity", language: "C#", level: "intermediate", middleware: "none", output: "WAV stems + seamless loop", constraints: "", goal: "", provider: "claude" });
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState("");
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v, ...(k === "engine" ? { language: LANGS[v] ?? f.language } : {}) }));

  const ask = async () => {
    if (form.goal.trim().length < 10) return toast.error("Describe your goal in a sentence or two.");
    setBusy(true); setAnswer("");
    try {
      const { data } = await api.post("/ai/integration-assistant", { ...form, middleware: form.middleware === "none" ? "" : form.middleware });
      for (let i = 0; i < 100; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: st } = await api.get(`/ai/integration-assistant/${data.id}`);
        if (st.status === "completed") { setAnswer(st.markdown); return; }
        if (st.status === "failed") throw new Error(st.error || "failed");
      }
      throw new Error("Timed out — please retry.");
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Assistant unavailable, please retry."); }
    finally { setBusy(false); }
  };

  return (
    <section className="space-y-8" data-testid="integration-guide">
      <div>
        <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Wand2 className="text-primary" size={22} /> Integration guide</h2>
        <p className="text-muted-foreground text-sm mt-1">From a clear brief to working code: what to prepare, how to ask, and an AI engineer that writes the integration for your engine.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {CHECKLIST.map((c) => (
          <div key={c.title} className="rounded-xl border border-border bg-card p-4 flex gap-3">
            <c.icon size={18} className="text-primary shrink-0 mt-0.5" />
            <div><p className="font-semibold text-sm">{c.title}</p><p className="text-xs text-muted-foreground mt-1 leading-relaxed">{c.text}</p></div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-background/60">
          <span className="font-mono text-xs text-muted-foreground">How to formulate your request (template)</span>
          <Button variant="ghost" size="sm" className="h-7 text-muted-foreground hover:text-primary" onClick={() => { navigator.clipboard.writeText(PROMPT_TEMPLATE); toast.success("Template copied."); }} data-testid="copy-prompt-template"><Copy size={13} className="mr-1" /> Copy</Button>
        </div>
        <pre className="p-4 text-xs font-mono whitespace-pre-wrap leading-relaxed text-foreground/85">{PROMPT_TEMPLATE}</pre>
      </div>

      <div className="rounded-2xl border border-primary/30 bg-primary/5 p-5 space-y-4" data-testid="integration-assistant">
        <div className="flex items-center gap-2"><Sparkles size={18} className="text-primary" /><h3 className="font-semibold">AI integration assistant</h3><span className="text-xs text-muted-foreground">— writes the code with the real MusicFlow SDK, free</span></div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Field label="Engine / framework"><Select value={form.engine} onValueChange={set("engine")}><SelectTrigger data-testid="assistant-engine"><SelectValue /></SelectTrigger><SelectContent>{ENGINES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent></Select></Field>
          <Field label="Language"><Input value={form.language} onChange={(e) => set("language")(e.target.value)} data-testid="assistant-language" /></Field>
          <Field label="Your level"><Select value={form.level} onValueChange={set("level")}><SelectTrigger data-testid="assistant-level"><SelectValue /></SelectTrigger><SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l} className="capitalize">{l}</SelectItem>)}</SelectContent></Select></Field>
          <Field label="Audio middleware"><Select value={form.middleware} onValueChange={set("middleware")}><SelectTrigger data-testid="assistant-middleware"><SelectValue /></SelectTrigger><SelectContent>{MIDDLEWARE.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></Field>
          <Field label="Expected output"><Input value={form.output} onChange={(e) => set("output")(e.target.value)} placeholder="WAV stems, seamless loop…" data-testid="assistant-output" /></Field>
          <Field label="Constraints" className="lg:col-span-3"><Input value={form.constraints} onChange={(e) => set("constraints")(e.target.value)} placeholder="latency < 200 ms, mobile build, offline fallback, 500 credits/month…" data-testid="assistant-constraints" /></Field>
        </div>
        <Field label="Goal & narrative moments to score">
          <Textarea rows={4} value={form.goal} onChange={(e) => set("goal")(e.target.value)} placeholder="Ex: leitmotif for the hero (major, 120 BPM, orchestral) and the villain (minor, 90 BPM, brass); trigger the boss theme when entering the arena, with a “tension” variation if the player failed the side quest." data-testid="assistant-goal" />
        </Field>
        <div className="flex items-center gap-3 flex-wrap">
          <Button onClick={ask} disabled={busy} className="bg-primary text-primary-foreground hover:bg-primary/90 gap-2" data-testid="assistant-submit">{busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} {busy ? "Writing your integration…" : "Generate my integration"}</Button>
          <Select value={form.provider} onValueChange={set("provider")}><SelectTrigger className="w-36 h-9" data-testid="assistant-provider"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="claude">Claude</SelectItem><SelectItem value="openai">ChatGPT</SelectItem></SelectContent></Select>
          <span className="text-xs text-muted-foreground">Takes 1-3 min (full, compilable code). Replace MUSICFLOW_API_KEY with your game key.</span>
        </div>
        {answer && (
          <div className="rounded-xl border border-border bg-card p-4" data-testid="assistant-answer">
            <div className="flex justify-end mb-1"><Button variant="ghost" size="sm" className="h-7 text-muted-foreground hover:text-primary" onClick={() => { navigator.clipboard.writeText(answer); toast.success("Answer copied."); }} data-testid="assistant-copy-all"><Copy size={13} className="mr-1" /> Copy all</Button></div>
            <Markdown text={answer} />
          </div>
        )}
      </div>

      <div>
        <h3 className="font-semibold mb-3">Best practices</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {PRACTICES.map(([t, d]) => <div key={t} className="rounded-xl border border-border bg-card p-4"><p className="text-sm font-semibold text-primary">{t}</p><p className="text-xs text-muted-foreground mt-1 leading-relaxed">{d}</p></div>)}
        </div>
      </div>
    </section>
  );
}

const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}><span className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">{label}</span><div className="mt-1">{children}</div></label>
);
